import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { api } from "../api.js";
import EntryModal from "../components/EntryModal.jsx";
import ErrorBanner from "../components/ErrorBanner.jsx";
import {
  listQueuedEntries,
  listFailedEntries,
  flushQueue,
  discardFailed,
  isLocalId,
} from "../offline.js";

const monthName = (m) =>
  [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ][m - 1];

export default function Today({ me, onGoSubmit }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ entries: [], totals: null, submission: null });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [editing, setEditing] = useState(null); // entry object or 'new' or null
  const [queued, setQueued] = useState([]);
  const [failed, setFailed] = useState([]);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [syncing, setSyncing] = useState(false);

  // Only show queued/failed entries that fall in the currently-viewed month,
  // so scrolling to prior months doesn't sprout phantom "pending" rows.
  const monthPrefix = useMemo(
    () => `${year}-${String(month).padStart(2, "0")}`,
    [year, month]
  );

  const refreshOffline = useCallback(async () => {
    const [q, f] = await Promise.all([listQueuedEntries(), listFailedEntries()]);
    setQueued(q.filter((e) => (e.date || "").startsWith(monthPrefix)));
    setFailed(f.filter((e) => (e.date || "").startsWith(monthPrefix)));
  }, [monthPrefix]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // Server entries (may fail offline — we catch below).
      const d = await api.listEntries(year, month);
      setData(d);
    } catch (e) {
      // Offline: still render whatever's queued, don't clobber prior data.
      if (!online) {
        setErr(null);
      } else {
        setErr(e.message);
      }
    } finally {
      setLoading(false);
    }
    await refreshOffline();
  }, [year, month, online, refreshOffline]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Drain the queue against the live API.
  const sync = useCallback(async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      await flushQueue((payload) => api.createEntry(payload));
    } finally {
      setSyncing(false);
      await refresh();
    }
  }, [syncing, refresh]);

  // Auto-flush on reconnect + on mount if we already have queued items.
  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      sync();
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [sync]);

  // Kick a sync on mount if anything is queued from a previous session.
  //
  // The naïve version depended only on `online`, which fires once on mount
  // while `queued` is still `[]` (refreshOffline hasn't finished yet), so
  // pre-existing queued items from the last session would sit there until
  // the user manually tapped Sync. We now watch `queued.length` as well,
  // but gate with a ref so repeated failures + refresh cycles don't loop
  // into an infinite retry storm. The onOnline listener above still covers
  // the offline→online transition.
  const firstSyncKicked = useRef(false);
  useEffect(() => {
    if (online && queued.length > 0 && !syncing && !firstSyncKicked.current) {
      firstSyncKicked.current = true;
      sync();
    }
  }, [online, queued.length, syncing, sync]);

  // Combine server entries with locally-queued entries for display + totals.
  // Defensive: the server always returns `entries` as an array, but if the
  // shape ever drifts (bad deploy, proxy rewrite, 200 non-JSON body) we'd
  // crash the whole screen on `[...data.entries]`. Coerce to an array.
  const serverEntries = Array.isArray(data?.entries) ? data.entries : [];
  const allEntries = useMemo(() => {
    return [...serverEntries, ...queued];
  }, [serverEntries, queued]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const e of allEntries) {
      const arr = m.get(e.date) || [];
      arr.push(e);
      m.set(e.date, arr);
    }
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [allEntries]);

  // Re-derive totals including queued trips so the Summary card stays honest.
  const displayTotals = useMemo(() => {
    const serverMiles = data.totals?.totalMiles ?? 0;
    const serverClaim = data.totals?.totalClaim ?? 0;
    const serverParking = data.totals?.totalParking ?? 0;
    const serverTolls = data.totals?.totalTolls ?? 0;
    // Use the server's rate if we have it; otherwise assume current federal rate.
    const rate = data.totals?.rate ?? 0.725;
    let qMiles = 0, qParking = 0, qTolls = 0;
    for (const e of queued) {
      qMiles += e.miles || 0;
      qParking += e.parking || 0;
      qTolls += e.tolls || 0;
    }
    const qClaim = qMiles * rate + qParking + qTolls;
    return {
      totalMiles: serverMiles + qMiles,
      totalParking: serverParking + qParking,
      totalTolls: serverTolls + qTolls,
      totalClaim: serverClaim + qClaim,
    };
  }, [data.totals, queued]);

  // Derive status from the *viewed* month's submission, not from
  // me.currentSubmission (which is locked to today's calendar month and
  // was stale anytime the worker navigated to a prior month). The server
  // now returns `submission` alongside entries for the same (year,month).
  const submitted = data.submission;
  const isApproved = submitted?.status === "approved";
  // A 'returned' submission means the supervisor kicked it back with a
  // reason — the month is editable again and the worker must resubmit
  // after making fixes. We treat it as "not submitted for review" from
  // the UI's perspective: the Submit button re-enables, edit guards
  // relax, and the banner explains what to do next.
  const isReturned = submitted?.status === "returned";
  const isPending = !!submitted && !isApproved && !isReturned;

  return (
    <div className="space-y-4 p-4">
      {/* Month switcher */}
      <div className="flex items-center justify-between">
        <button
          className="tap rounded-md px-2 text-slate-600 active:bg-slate-100"
          onClick={() => {
            let y = year, m = month - 1;
            if (m < 1) { m = 12; y--; }
            setYear(y); setMonth(m);
          }}
          aria-label="Previous month"
        >
          ‹
        </button>
        <div className="text-lg font-semibold text-slate-900">
          {monthName(month)} {year}
        </div>
        <button
          className="tap rounded-md px-2 text-slate-600 active:bg-slate-100"
          onClick={() => {
            let y = year, m = month + 1;
            if (m > 12) { m = 1; y++; }
            setYear(y); setMonth(m);
          }}
          aria-label="Next month"
        >
          ›
        </button>
      </div>

      {/* Offline / pending-sync banner */}
      {(!online || queued.length > 0 || failed.length > 0) && (
        <div
          className={`rounded-lg px-3 py-2 text-sm ${
            !online
              ? "bg-amber-50 text-amber-900"
              : queued.length > 0
              ? "bg-sky-50 text-sky-900"
              : "bg-red-50 text-red-700"
          }`}
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              {!online && (
                <>
                  <b>Offline.</b> New trips will sync automatically when you
                  reconnect.
                </>
              )}
              {online && queued.length > 0 && (
                <>
                  <b>{queued.length}</b> trip{queued.length === 1 ? "" : "s"}
                  {" "}waiting to sync.
                </>
              )}
              {online && queued.length === 0 && failed.length > 0 && (
                <>
                  <b>{failed.length}</b> trip{failed.length === 1 ? "" : "s"}
                  {" "}could not sync — see below.
                </>
              )}
            </div>
            {online && queued.length > 0 && (
              <button
                className="tap rounded-md bg-white px-3 py-1 text-sm font-semibold text-sky-900 ring-1 ring-sky-200 active:bg-sky-100 disabled:opacity-50"
                onClick={sync}
                disabled={syncing}
              >
                {syncing ? "Syncing…" : "Sync now"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Summary card */}
      <div className="card">
        {loading ? (
          <div className="text-slate-500">Loading entries…</div>
        ) : (
          <div className="grid grid-cols-3 gap-3 text-center">
            <Metric label="Trips" value={allEntries.length} />
            <Metric label="Miles" value={displayTotals.totalMiles.toFixed(1)} />
            <Metric
              label="Claim"
              value={`$${displayTotals.totalClaim.toFixed(2)}`}
            />
          </div>
        )}
        {isApproved && (
          <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Approved on {submitted.approvedAt?.slice(0, 10)}. This month is
            locked.
          </div>
        )}
        {isPending && (
          <div className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
            Submitted — waiting for supervisor approval.
          </div>
        )}
        {isReturned && (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <div className="font-semibold">
              Returned for revisions{" "}
              {submitted.returnedAt?.slice(0, 10)
                ? `(${submitted.returnedAt.slice(0, 10)})`
                : ""}
            </div>
            <p className="mt-1 text-xs">
              Your supervisor sent this back — fix the entries below and tap{" "}
              <b>Submit month</b> again.
            </p>
            {submitted.returnedReason && (
              <div className="mt-2 whitespace-pre-wrap rounded bg-white/70 px-2 py-1 text-xs">
                {submitted.returnedReason}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          className="btn-primary flex-1"
          onClick={() => setEditing("new")}
          // Mirror the server-side guard: the POST /api/entries endpoint
          // rejects adds with 409 while the month is submitted OR approved.
          // Without disabling here, a worker taps Add, fills out the form,
          // then bounces off a 409 — a dead-end UX. Returned months are
          // editable again.
          disabled={isApproved || isPending}
          title={
            isApproved
              ? "This month is approved and locked"
              : isPending
              ? "Waiting for supervisor approval — ask them to return it if you need to add trips"
              : undefined
          }
        >
          + Add trip
        </button>
        <button
          className="btn-secondary"
          onClick={() => onGoSubmit({ year, month })}
          // Block submit while anything is still local-only — the server
          // doesn't know about those trips yet. 'returned' means the
          // supervisor reopened the month, so resubmit is allowed.
          disabled={data.entries.length === 0 || isPending || isApproved || queued.length > 0}
          title={
            queued.length > 0
              ? "Sync pending trips before submitting the month"
              : undefined
          }
        >
          {isReturned ? "Resubmit month" : "Submit month"}
        </button>
      </div>

      {/* `err` is only set when we're online — offline fetch failures are
          swallowed in refresh() since the queued/failed banners cover that
          case. So an error here truly merits a Retry button. */}
      <ErrorBanner message={err} onRetry={refresh} />

      {/* Entry list */}
      <div className="space-y-3">
        {grouped.length === 0 && !loading && (
          <div className="rounded-xl border-2 border-dashed border-slate-300 bg-white p-6 text-center text-sm text-slate-500">
            No trips logged yet for {monthName(month)}.
            <br />
            Tap <b>+ Add trip</b> to log your first one.
          </div>
        )}
        {grouped.map(([date, entries]) => (
          <div key={date} className="card">
            <div className="mb-2 text-sm font-semibold text-slate-700">
              {formatDate(date)}
            </div>
            <ul className="divide-y divide-slate-100">
              {entries.map((e) => {
                const pending = e.pending || isLocalId(e.id);
                return (
                  <li
                    key={e.id}
                    className="flex items-start justify-between gap-3 py-2"
                  >
                    <button
                      className="tap flex-1 text-left"
                      // Same reasoning as "+ Add trip" above: edit + delete
                      // both 409 while the month is submitted. Block the tap
                      // so we don't sink the worker into a failed-save loop.
                      onClick={() => !isApproved && !isPending && setEditing(e)}
                      disabled={isApproved || isPending}
                    >
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-slate-900">
                          {e.origin} → {e.destination}
                        </div>
                        {pending && (
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                            Pending sync
                          </span>
                        )}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-500">
                        {e.miles.toFixed(1)} mi
                        {e.parking > 0 && ` · $${e.parking.toFixed(2)} parking`}
                        {e.tolls > 0 && ` · $${e.tolls.toFixed(2)} tolls`}
                      </div>
                      {e.purpose && (
                        <div className="mt-0.5 text-xs italic text-slate-500">
                          {e.purpose}
                        </div>
                      )}
                    </button>
                    {!isApproved && !isPending && (
                      <span className="text-slate-300">›</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}

        {failed.length > 0 && (
          <div className="card border border-red-200 bg-red-50/50">
            <div className="mb-2 text-sm font-semibold text-red-800">
              Could not sync ({failed.length})
            </div>
            <p className="mb-2 text-xs text-red-700">
              These trips were rejected by the server — usually because the
              month was already approved. They are not counted in your totals.
            </p>
            <ul className="divide-y divide-red-100">
              {failed.map((e) => (
                <li key={e.id} className="py-2">
                  <div className="text-sm font-medium text-slate-900">
                    {formatDate(e.date)} — {e.origin} → {e.destination}
                  </div>
                  <div className="mt-0.5 text-xs text-slate-500">
                    {e.miles.toFixed(1)} mi · {e.error || "Server rejected"}
                  </div>
                  <button
                    className="mt-1 text-xs font-semibold text-red-700 underline active:text-red-900"
                    onClick={async () => {
                      await discardFailed(e.id);
                      await refreshOffline();
                    }}
                  >
                    Discard
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {editing && (
        <EntryModal
          mode={editing === "new" ? "create" : "edit"}
          entry={editing === "new" ? null : editing}
          defaultDate={isoToday()}
          priorEntries={data.entries}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await refresh();
          }}
          onDeleted={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div>
      <div className="text-2xl font-bold tabular-nums text-slate-900">
        {value}
      </div>
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
    </div>
  );
}

function isoToday() {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function formatDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}
